import React, { useEffect, useState } from 'react';
import * as z from 'zod';
import {
  ArrowRight, Backpack, BadgeCheck, LucideSkipBack, LucideStepBack, SendToBackIcon,
  SkipBack,
  StepBack,
  StepForward,
  Undo2
} from 'lucide-react';

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { format, set } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { toast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@/lib/utils';
import { useUserInfo } from "@/hooks/useUserInfo.ts";
import { getSummary, sendRemoteWorkRequest, getLeaveTypes, getLeaveTypeBalance, sendLeaveApplicationRequest } from "@/utils/pasUtils.js";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import TimePicker from 'react-time-picker';
import 'react-time-picker/dist/TimePicker.css'; // Import default styles
import 'react-clock/dist/Clock.css'; // Required for react-time-picker
import { get } from 'http';

const formSchema = z.object({
  employee: z.string({
    required_error: 'الموظف is required.'
  }),
  leave_type: z.string({
    required_error: 'من يوم is required.'
  }),
  from_date: z.string({
    required_error: 'من يوم is required.'
  }),
  to_date: z.string({
    required_error: 'إلى يوم is required.'
  }),
  half_day: z.number({ required_error: 'نصف يوم is required.' }).optional(),

  description: z.string({
    required_error: ' الوصف is required.'
  }).min(1, " الوصف مطلوب"),
  company: z.string().optional(),
});


const ServiceLeaveRequest = () => {
  const VITE_USERNAMET = import.meta.env.VITE_USERNAMET
  const VITE_PASSWORD = import.meta.env.VITE_PASSWORD
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState({
    profile: null,
    loading: true,
    error: null

  });
  const { greeting, fullName, email, role, profilePictureUrl } = useUserInfo();
  const [selectedFromDate, setSelectedFromDate] = useState<Date | null>(null);
  const [selectedToDate, setSelectedToDate] = useState<Date | null>(null);
  const [totalHours, setTotalHours] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [leaveTypeBalance, setLeaveTypeBalance] = useState(0);
  const [selectedLeaveType, setSelectedLeaveType] = useState('');
  const [totalDay, setTotalDay] = useState(0);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      employee: data.profile ? `${data.profile.name}` : '',
      leave_type: '',
      from_date: '',
      to_date: '',
      half_day: 0,
      description: '',
      company: "WSM"
    },
  });
  const [leaveTypes, setLeaveTypes] = useState(null);
  useEffect(() => {
    async function fetchAttendance() {
      try {
        setLoading(true);

        if (email) {
          // Fetch summary data based on email    
          const summaryResponse = await getSummary(
            VITE_USERNAMET,
            VITE_PASSWORD,
            email
          );
          setEmployeeId(summaryResponse.profile.name);
          const leaveTypesData = await getLeaveTypes(VITE_USERNAMET,
            VITE_PASSWORD); // Fetch leave types
          // Transform the nested array structure
          const transformedData = leaveTypesData.map((item, index) => {
            const typeString = item[0];
            return {
              id: index.toString(),
              value: typeString,
              label: typeString
            };
          });

          setLeaveTypes(transformedData);
          // setLeaveTypes(leaveTypesData);
          console.log("Leave types:", leaveTypesData);
          if (data.loading) {
            setData((prev) => ({
              ...prev,
              profile: summaryResponse.profile,
              loading: false,
              error: null,
            }));
            console.log("Profile data:", data);
            form.reset({
              employee: `${summaryResponse.profile.name}`,
              leave_type: '',
              from_date: '',
              to_date: '',
              half_day: 0,
              description: '', // Set actual start time
              company: 'WSM',   // Set actual end time
            });

          }
        }
      } catch (err) {
        setError(err.message || "Failed to fetch attendance");
        console.log(err.message || "Failed to fetch attendance");
      } finally {
        setLoading(false);
        console.log("Profile data:", data.profile);
      }
    }

    fetchAttendance();
  }, [data, email, form, totalHours]);


  const calculateTotalDays = (): number => {
    const fromDate = form.getValues('from_date');
    const toDate = form.getValues('to_date');
    if (!fromDate || !toDate) return 0;

    const from = new Date(fromDate);
    const to = new Date(toDate);

    const timeDiff = to.getTime() - from.getTime();
    const dayDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // +1 to include both start and end dates

    setTotalDay(dayDiff > 0 ? dayDiff : 0);
    return dayDiff > 0 ? dayDiff : 0;
  };

  async function onSubmit(values: z.infer<typeof formSchema>) {
    console.log("Form values:", values);
    if (leaveTypeBalance < totalDay) {
      toast({
        title: `لا يوجد رصيد إجازات متبقي لهذا النوع (${form.getValues('leave_type')})، الرصيد المتبقي هو ${leaveTypeBalance} أيام.`,
        description: 'يرجى اختيار نوع إجازة آخر أو التواصل مع الموارد البشرية.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const response = await sendLeaveApplicationRequest(VITE_USERNAMET,
        VITE_PASSWORD,
        {
          employee: form.getValues('employee'),
          leave_type: form.getValues('leave_type'),
          from_date: form.getValues('from_date'),
          to_date: form.getValues('to_date'),
          half_day: form.getValues('half_day'),
          description: form.getValues('description'),
          company: form.getValues('company'),
        });
      
        toast({
          title: 'تم إرسال طلب الإجازة بنجاح',
          description: 'سيتم مراجعة طلبك من قبل الإدارة.',
          duration: 5000,
        });
        navigate('/services');
    } catch (error) {
      console.error("Request failed:", error);
      toast({
          title: 'فشل في إرسال طلب الإجازة',
          description: error.message || 'حدث خطأ أثناء معالجة طلبك. يرجى المحاولة مرة أخرى.',
          duration: 5000,
          variant: 'destructive',
        });
    }
  }


  async function handleLeaveTypeChange(value: string) {
    const leaveTypeBalance = await getLeaveTypeBalance(VITE_USERNAMET,
      VITE_PASSWORD, employeeId, value); // Fetch leave types
    setLeaveTypeBalance(leaveTypeBalance);
    setSelectedLeaveType(value);
    console.log("selected Leave type:", selectedLeaveType);
    console.log("Leave Balance types:", leaveTypeBalance);
  }

  return (
    <>
      {data.loading ? (
        <div>Loading...</div>
      ) : data.profile ? (
        <div className="w-full max-w-4xl">
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate('/services')}
              className="gap-2"
            >
              <ArrowRight className="h-4 w-4" />
              العودة للخدمات
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-right">{t('services-leave-application.title')}</CardTitle>
              <CardDescription className="text-right">
                يرجى ملء جميع البيانات المطلوبة لتقديم {t('services-leave-application.title')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                    {data.profile.name && <FormField
                      control={form.control}
                      name="employee"
                      rules={{
                        required: "الموظف is required." // Custom error message
                      }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-right block">الموظف</FormLabel>
                          <FormControl>
                            <Input
                              readOnly={true}
                              placeholder="الموظف..."
                              className="text-right"
                              {...field}
                            />
                          </FormControl>

                          <FormMessage />
                        </FormItem>
                      )}
                    />}
                    <FormField
                      control={form.control}
                      name="leave_type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-right block">نوع الإجازة</FormLabel>
                          <Select onValueChange={(value) => {
                            field.onChange(value);
                            handleLeaveTypeChange(value);
                            console.log("Selected leave type:", value);
                          }}
                            defaultValue={field.value} value={selectedLeaveType}
                          >
                            <FormControl>
                              <SelectTrigger className="w-full p-2 border border-gray-300 rounded-md text-right flex justify-between flex-row-reverse">
                                <SelectValue placeholder="اختر نوع الإجازة" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {leaveTypes && leaveTypes.map((type) => (
                                <SelectItem key={type.id} value={type.value}>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="from_date"
                      rules={{
                        required: "من يوم مطلوب"
                      }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-right block">من يوم</FormLabel>
                          <FormControl>
                            <DatePicker
                              selected={selectedFromDate}
                              onChange={(date: Date | null) => {
                                setSelectedFromDate(date);
                                // field.onChange(date ? date.toISOString().split('T')[0] : '');
                                const dateString = date.toLocaleDateString('en-CA'); // 'en-CA' gives YYYY-MM-DD
                                field.onChange(dateString);
                                calculateTotalDays();
                              }}
                              dateFormat="yyyy/MM/dd"
                              placeholderText="اختر من يوم..."
                              className="w-full p-2 border border-gray-300 rounded-md text-right"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="to_date"
                      rules={{
                        required: "إلى يوم مطلوب"
                      }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-right block">إلى يوم</FormLabel>
                          <FormControl>
                            <DatePicker
                              selected={selectedToDate}
                              onChange={(date: Date | null) => {
                                setSelectedToDate(date);
                                // field.onChange(date ? date.toISOString().split('T')[0] : '');
                                 const dateString = date.toLocaleDateString('en-CA'); // 'en-CA' gives YYYY-MM-DD
                                field.onChange(dateString);
                                calculateTotalDays();
                              }}
                              dateFormat="yyyy/MM/dd"
                              placeholderText="اختر إلى يوم..."
                              className="w-full p-2 border border-gray-300 rounded-md text-right"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="description"
                      rules={{
                        required: "الوصف is required." // Custom error message
                      }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-right block">الوصف</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="الوصف..."
                              className="text-right"
                              {...field}
                            />
                          </FormControl>

                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField

                      control={form.control}
                      name="half_day"
                      rules={{
                        required: "نصف يوم is required." // Custom error message
                      }}
                      render={({ field }) => (
                        <FormItem dir='rtl'>
                          <FormLabel className="text-right block">نصف يوم</FormLabel>
                          <FormControl>
                            <Input
                              type="checkbox"
                              // className="text-right text-sm"
                              // {...field}
                              checked={field.value === 1} // Convert number to boolean
                              onChange={(e) => {
                                // Convert boolean to number (0 or 1)
                                field.onChange(e.target.checked ? 1 : 0);
                              }}
                              className="w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary focus:ring-2"
                            />
                          </FormControl>

                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="company"
                      rules={{
                        required: "الشركة is required." // Custom error message
                      }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-right block">الشركة</FormLabel>
                          <FormControl>
                            <Input
                              type="text"
                              placeholder="الشركة..."
                              readOnly={true}
                              className="text-right text-lg"
                              {...field}

                            />
                          </FormControl>

                          <FormMessage />
                        </FormItem>
                      )}
                    />

                  </div>


                  <div className="flex gap-4 justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate('/services')}
                    >
                      إلغاء
                    </Button>
                    {/* <Button type="submit" disabled={isSaved}>
                  حفظ
                </Button> */}
                    <Button type="submit"
                    // onClick={handlePrint}
                    >
                      إرسال الطلب
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div >
      ) : (
        <div>No profile data available</div>
      )}
    </>
  );
};

export default ServiceLeaveRequest;